import { Autocomplete, InputAdornment, SvgIcon, TextField } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface SearchableDropdownProps<T extends { id: string }> {
  options: T[];
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  value: T | null;
  inputValue: string;
  loading?: boolean;
  onInputChange: (value: string) => void;
  onChange: (value: T | null) => void;
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export const SearchableDropdown = <T extends { id: string }>({
  options,
  getOptionLabel,
  isOptionEqualToValue = (opt, val) => opt.id === val.id,
  value,
  inputValue,
  loading = false,
  onInputChange,
  onChange,
  label,
  required = false,
  error = false,
  helperText,
  disabled = false,
  sx,
}: SearchableDropdownProps<T>) => (
  <Autocomplete
    sx={sx}
    options={options}
    getOptionKey={(opt) => opt.id}
    getOptionLabel={getOptionLabel}
    isOptionEqualToValue={isOptionEqualToValue}
    value={value}
    inputValue={inputValue}
    loading={loading}
    filterOptions={(x) => x}
    disabled={disabled}
    onInputChange={(_, val) => onInputChange(val)}
    onChange={(_, val) => onChange(val)}
    renderInput={({ InputProps, inputProps, InputLabelProps, id, disabled: inputDisabled, fullWidth }) => (
      <TextField
        id={id}
        disabled={inputDisabled}
        fullWidth={fullWidth}
        label={required ? `${label} *` : label}
        size="small"
        error={error}
        helperText={helperText}
        slotProps={{
          input: {
            ...InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0 }}>
                <SvgIcon sx={{ fontSize: '1.25rem' }} viewBox="0 0 24 24">
                  <path d="M15.755 14.2549H14.965L14.685 13.9849C15.665 12.8449 16.255 11.3649 16.255 9.75488C16.255 6.16488 13.345 3.25488 9.755 3.25488C6.165 3.25488 3.255 6.16488 3.255 9.75488C3.255 13.3449 6.165 16.2549 9.755 16.2549C11.365 16.2549 12.845 15.6649 13.985 14.6849L14.255 14.9649V15.7549L19.255 20.7449L20.745 19.2549L15.755 14.2549ZM9.755 14.2549C7.26501 14.2549 5.255 12.2449 5.255 9.75488C5.255 7.26488 7.26501 5.25488 9.755 5.25488C12.245 5.25488 14.255 7.26488 14.255 9.75488C14.255 12.2449 12.245 14.2549 9.755 14.2549Z" />
                </SvgIcon>
              </InputAdornment>
            ),
          } as object,
          htmlInput: inputProps,
          inputLabel: InputLabelProps,
          formHelperText: { sx: { ml: '5px' } },
        }}
      />
    )}
  />
);
